import type { FitZoneState } from '@/types/domain';

export interface FitSummary {
  zone: 'tight' | 'neutral' | 'loose';
  text: string;
}

const REGIONS = ['chest', 'waist', 'hip'] as const;

function joinRegions(regions: string[]): string {
  if (regions.length === 1) return regions[0];
  if (regions.length === 2) return `${regions[0]} and ${regions[1]}`;
  return `${regions.slice(0, -1).join(', ')}, and ${regions[regions.length - 1]}`;
}

/**
 * A single-line, human summary of a comparison's fit zones — used on the
 * compact Fit Match cards where there isn't room for the full measurement
 * breakdown. Derived entirely from the real zones already computed by the
 * comparison engine; never a separate/invented judgement. Tight zones take
 * priority over loose ones since a too-small recommendation is the more
 * actionable thing to flag.
 */
export function summarizeFit(zones: FitZoneState): FitSummary {
  const tightRegions = REGIONS.filter((r) => zones[r] === 'tight');
  if (tightRegions.length > 0) return { zone: 'tight', text: `Fitted in ${joinRegions(tightRegions)}` };

  const looseRegions = REGIONS.filter((r) => zones[r] === 'loose');
  if (looseRegions.length > 0) return { zone: 'loose', text: `Roomier in ${joinRegions(looseRegions)}` };

  const hasAnyActive = REGIONS.some((r) => zones[r] !== 'inactive');
  return hasAnyActive ? { zone: 'neutral', text: 'True to size' } : { zone: 'neutral', text: 'Limited comparable data' };
}
